/**
 * As apólices de saúde da família, em tabela.
 *
 * Papel: mostra uma linha por apólice, com a foto da carteirinha, a pessoa, a
 * operadora, o plano, o número da carteirinha, o ingresso, a validade e o CNS,
 * mais os botões de editar e remover.
 *
 * Como usar: quem monta a tabela passa `onEditar` e `onRemover` apenas para
 * quem tem nível `editar`; sem eles a coluna de ações some inteira. O
 * `podeAlterarFoto` segue o mesmo nível, e sem ele o modal da carteirinha
 * abre sem o "Alterar foto".
 *
 * A pessoa aparece pelo nome completo com o apelido entre parênteses, por
 * `formatarPessoa`, a mesma forma que o aviso de vencimento dá à linha do
 * plano; escrever o nome aqui de outro jeito faria a mesma apólice aparecer
 * com dois nomes na tabela e no aviso.
 *
 * A carteirinha e o CNS aparecem como estão gravados, sem máscara: os espaços
 * de `12345678 00 01 2` são o formato da operadora, e o CNS é uma sequência de
 * quinze dígitos que a operadora e o SUS leem sem pontuação.
 *
 * A primeira coluna, "Carteirinha", mostra a foto em miniatura de 56×35 px,
 * que cresce sob o cursor e, clicada, abre o `ModalDaCarteirinha` com a imagem
 * grande. O modal acompanha a apólice pelo `id`: quando a lista volta depois
 * de uma troca de foto, ele passa a mostrar a apólice nova. Cada linha com
 * `foto` baixa a própria imagem por `useFotoDaCarteirinha`; a linha sem `foto`
 * mostra o travessão sem ir à API. A coluna não ordena: não há o que comparar
 * entre duas imagens.
 *
 * Os números — carteirinha, datas e CNS — saem na fonte do texto, com
 * algarismos de largura fixa (`tabular-nums`), e não na mono da `.tabular`:
 * é a fonte da origem, e a mono, mais larga, empurra o CNS e as ações para fora
 * da tabela em 1280 px depois que a coluna da foto entrou.
 *
 * A marca de vencida ao lado da validade sai de `jaVenceu`, a mesma função que
 * a apuração do aviso usa. Escrever a comparação aqui faria a apólice aparecer
 * vencida num lugar e em dia no outro no dia em que uma das duas mudasse.
 *
 * Cada cabeçalho, menos o da foto e o das ações, ordena a tabela pela regra
 * `'com-retorno'` de `useOrdenacao`: crescente, decrescente e de volta à ordem
 * em que `planos` chegou, com o vazio sempre no fim. A pessoa ordena pelo texto
 * que a célula mostra; Ingresso e Validade, como data; a carteirinha e o CNS,
 * como texto.
 */
import { type PlanoSaude } from '@concierge/shared';
import { useState } from 'react';

import {
  CabecalhoOrdenavel,
  type ColunaOrdenavel,
  SETA_EM_TEXTO,
  useOrdenacao,
} from '@/shared/embutido';
import { formatarData, formatarPessoa } from '@/shared/lib/formato';
import { jaVenceu } from '@/shared/lib/vencimento';
import { Button } from '@/shared/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { temFoto } from '../foto-da-carteirinha';
import { useFotoDaCarteirinha } from '../hooks/usar-foto-da-carteirinha';
import { ModalDaCarteirinha } from './modal-da-carteirinha';

interface TabelaPlanosProps {
  planos: PlanoSaude[];
  /** A data de hoje em `AAAA-MM-DD`, para decidir o que já venceu. */
  hoje: string;
  /** Chamado com a apólice da linha; ausente esconde a coluna de ações. */
  onEditar?: (plano: PlanoSaude) => void;
  /** Chamado com a apólice a excluir; a confirmação é de quem monta. */
  onRemover?: (plano: PlanoSaude) => void;
  /** Verdadeiro para quem tem `editar` em saúde: o modal mostra "Alterar foto". */
  podeAlterarFoto?: boolean;
}

/** As colunas ordenáveis, na ordem e com o rótulo do cabeçalho. */
const COLUNAS: Record<string, ColunaOrdenavel<PlanoSaude> & { rotulo: string }> = {
  pessoa: { rotulo: 'Pessoa', tipo: 'texto', valor: (p) => formatarPessoa(p.pessoa) },
  operadora: { rotulo: 'Operadora', tipo: 'texto', valor: (p) => p.operadora },
  plano: { rotulo: 'Plano', tipo: 'texto', valor: (p) => p.plano },
  carteirinha: { rotulo: 'Nº Carteirinha', tipo: 'texto', valor: (p) => p.carteirinha },
  ingresso: { rotulo: 'Ingresso', tipo: 'data', valor: (p) => p.ingresso },
  validade: { rotulo: 'Validade', tipo: 'data', valor: (p) => p.validade },
  cns: { rotulo: 'CNS', tipo: 'texto', valor: (p) => p.cns },
};

/**
 * A miniatura da carteirinha de uma linha, ou o que a substitui.
 *
 * Enquanto a imagem é baixada, a caixa tracejada ocupa o tamanho exato da
 * miniatura, para a linha não mudar de altura quando a imagem chegar.
 *
 * @param plano - A apólice da linha; sem `foto`, nada é baixado.
 * @param pessoa - A pessoa como a linha a escreve, para o texto alternativo e
 *   o nome do botão.
 * @param onAbrir - Chamado no clique na miniatura, para abrir o modal.
 * @returns O botão com a miniatura, a caixa de espera ou o travessão.
 */
function MiniaturaDaCarteirinha({
  plano,
  pessoa,
  onAbrir,
}: {
  plano: PlanoSaude;
  pessoa: string;
  onAbrir: () => void;
}) {
  const { url, carregando, falhou } = useFotoDaCarteirinha(plano);

  if (falhou) {
    return (
      <span className="text-texto-fraco" title="Não foi possível carregar a foto.">
        —
      </span>
    );
  }
  if (carregando) {
    return (
      <span
        role="status"
        aria-label="Carregando a foto"
        className="block h-[35px] w-[56px] rounded-[3px] border border-dashed border-linha-forte"
      />
    );
  }
  if (url === null) return <span className="text-texto-fraco">—</span>;

  return (
    <button
      type="button"
      aria-label={`Abrir a carteirinha de ${pessoa}`}
      className="block cursor-pointer"
      onClick={onAbrir}
    >
      <img
        src={url}
        alt={`Carteirinha ${pessoa}`}
        className="block h-[35px] w-[56px] cursor-pointer rounded-[3px] border border-linha-forte object-cover transition-transform duration-150 ease-in-out hover:relative hover:z-[5] hover:scale-[1.6] hover:shadow-miniatura"
      />
    </button>
  );
}

/**
 * A tabela de apólices.
 *
 * @param planos - As apólices a mostrar, na ordem que vale antes de qualquer
 *   clique e à qual o terceiro clique devolve.
 * @param hoje - A data de hoje, de onde sai a marca de vencido.
 * @param onEditar - Chamado ao clicar em editar; ausente esconde as ações.
 * @param onRemover - Chamado ao clicar em remover; ausente esconde as ações.
 * @param podeAlterarFoto - Verdadeiro quando o nível em saúde é `editar`;
 *   ausente, o modal da carteirinha só mostra e baixa a foto.
 * @returns A tabela com uma linha por apólice, e o modal da carteirinha
 *   quando uma miniatura foi clicada.
 */
export function TabelaPlanos({
  planos,
  hoje,
  onEditar,
  onRemover,
  podeAlterarFoto = false,
}: TabelaPlanosProps) {
  const [idDaCarteirinha, setIdDaCarteirinha] = useState<string | null>(null);
  const mostrarAcoes = onEditar !== undefined || onRemover !== undefined;
  const ordenacao = useOrdenacao(planos, COLUNAS, 'com-retorno');
  const carteirinhaAberta = planos.find((plano) => plano.id === idDaCarteirinha);

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Carteirinha</TableHead>
            {Object.entries(COLUNAS).map(([chave, coluna]) => (
              <CabecalhoOrdenavel
                key={chave}
                como={TableHead}
                title={`Ordenar por ${coluna.rotulo}`}
                seta={ordenacao.seta(chave)}
                classeDaSeta={SETA_EM_TEXTO}
                onAlternar={() => ordenacao.alternar(chave)}
              >
                {coluna.rotulo}
              </CabecalhoOrdenavel>
            ))}
            {mostrarAcoes && (
              <TableHead>
                <span className="sr-only">Ações</span>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {ordenacao.linhas.map((apolice) => {
            const vencida = jaVenceu(apolice.validade, hoje);
            const pessoa = formatarPessoa(apolice.pessoa);

            return (
              <TableRow key={apolice.id}>
                <TableCell>
                  <MiniaturaDaCarteirinha
                    plano={apolice}
                    pessoa={pessoa}
                    onAbrir={() => setIdDaCarteirinha(apolice.id)}
                  />
                </TableCell>
                <TableCell className="text-texto-medio">{pessoa}</TableCell>
                <TableCell>{apolice.operadora}</TableCell>
                <TableCell>{apolice.plano}</TableCell>
                <TableCell className="tabular-nums">{apolice.carteirinha}</TableCell>
                <TableCell className="tabular-nums">
                  {apolice.ingresso === null ? '—' : formatarData(apolice.ingresso)}
                </TableCell>
                <TableCell className="tabular-nums">
                  {apolice.validade === null ? '—' : formatarData(apolice.validade)}
                  {vencida && (
                    <span className="ml-2 text-xs font-semibold text-destructive">
                      vencida
                    </span>
                  )}
                </TableCell>
                <TableCell className="tabular-nums">{apolice.cns ?? '—'}</TableCell>
                {mostrarAcoes && (
                  <TableCell>
                    <div className="flex gap-2">
                      {onEditar !== undefined && (
                        <Button variant="outline" size="sm" onClick={() => onEditar(apolice)}>
                          editar
                        </Button>
                      )}
                      {onRemover !== undefined && (
                        <Button variant="destructive" onClick={() => onRemover(apolice)}>
                          remover
                        </Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {carteirinhaAberta !== undefined && temFoto(carteirinhaAberta) && (
        <ModalDaCarteirinha
          key={carteirinhaAberta.id}
          plano={carteirinhaAberta}
          podeAlterar={podeAlterarFoto}
          onFechar={() => setIdDaCarteirinha(null)}
        />
      )}
    </>
  );
}
